import { useCallback, useState } from 'react';
import { A02 } from 'api/U002';
import { FetchError } from 'api/API';

type CreateTemporaryUserRequest = {
  phoneNumber: string;
  path: string;
  term_of_service_consent: string;
  privacy_notice_consent: string;
  specific_use_consent: string;
  birth: Date | null;
  lang: string;
  status?: string;
  session_id?: string;
  recaptcha_token: string;
};
type CreateTemporaryUserResponse = {
  status: string;
  msgid: string;
};

type CreateTemporaryUserDoRequest = (param: {
  request: CreateTemporaryUserRequest;
  onSuccess?: (response: CreateTemporaryUserResponse) => void;
  onError?: (err: Error) => void;
  cancellation?: AbortSignal;
}) => Promise<void>;

export function useCreateTemporaryUser(): [CreateTemporaryUserDoRequest, boolean, Error | null] {
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setError] = useState<Error | null>(null);

  const createTemporaryUser = async (
    request: CreateTemporaryUserRequest,
    cancellation: AbortSignal
  ) => {
    const json = await A02(request, cancellation);
    return json;
  };

  const doRequest = useCallback<CreateTemporaryUserDoRequest>(
    async ({ request, cancellation, onSuccess, onError }) => {
      const controller = new AbortController();
      const cancelSignal = cancellation ?? controller.signal;
      try {
        const result = await createTemporaryUser(request, cancelSignal);
        onSuccess?.(result);
      } catch (err) {
        console.error({ err });
        if (err instanceof FetchError) {
          const errorData = await err.response.json();
          const flooredError = new Error(errorData.message || `HTTP ERROR: ${err.message}`);
          onError?.(flooredError);
          setError(flooredError);
        } else {
          const flooredError = new Error(`unknown error. ${err}`);
          onError?.(flooredError);
          setError(flooredError);
        }
      } finally {
        setLoading(false);
      }
    },
    [createTemporaryUser, setLoading, setError]
  );

  return [doRequest, loading, err];
}
